<template>
  <v-dialog :value="value" @input="!$event && !saving && $emit('close')" :persistent="saving" max-width="700px" scrollable>
    <v-card class="care-edit-card">
      <v-card-title>
        <div class="care-edit-title"><span class="headline">編輯發送時間</span><div v-if="currentItem" class="text-subtitle-2 mt-1">{{ currentItem.tag_name }}</div></div>
      </v-card-title>
      <v-card-text>
        <p v-if="editableMessages.some(msg => msg.canResend || msg.resendIntent)" class="text-caption mb-3">
          重新安排會以原有訊息內容新增一筆待發送排程，原紀錄會保留。儲存成功代表排程已建立，尚未發送。
        </p>
        <div v-for="(msg, index) in editableMessages" :key="msg.id" class="care-edit-row my-3">
          <div class="d-flex align-center justify-space-between" style="gap: 8px;">
            <span class="care-edit-title">{{ msg.title }}</span>
            <v-btn v-if="msg.isPotentiallyEditable && !msg.resendResult && !msg.batchSubmitted"
              :disabled="saving || !!(msg.resendIntent && msg.resendIntent.uncertain)"
              :aria-label="msg.isEditing ? '取消編輯此訊息' : (msg.canResend ? '重新安排此訊息' : '編輯此訊息')"
              small text color="primary" @click="$emit('toggle-edit', msg)">
              {{ msg.isEditing ? '取消編輯' : (msg.canResend ? '重新安排' : '編輯') }}
            </v-btn>
          </div>
          <div v-if="msg.isEditing" class="care-edit-fields mt-2">
            <v-select v-if="msg.canResend && msg.canCancel" v-model="msg.operation" :items="operationOptions"
              :disabled="saving || !!(msg.resendIntent && msg.resendIntent.uncertain)" label="操作" dense outlined hide-details
              class="care-edit-operation" @change="changeOperation(msg)"/>
            <v-menu v-model="datePickerMenus[index]" :disabled="isTimeDisabled(msg)" :close-on-content-click="false"
              transition="scale-transition" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field v-model="msg.date" :disabled="isTimeDisabled(msg)" label="日期" readonly v-bind="attrs" v-on="on"
                  dense outlined hide-details class="care-edit-date"/>
              </template>
              <v-date-picker v-model="msg.date" :disabled="isTimeDisabled(msg)" scrollable locale="zh"
                :min="minDate" @input="datePickerMenus[index] = false"/>
            </v-menu>
            <div class="care-edit-time">
              <v-select v-model="msg.hour" :disabled="isTimeDisabled(msg)" :items="hours" label="時" dense outlined hide-details/>
              <span>:</span>
              <v-select v-model="msg.minute" :disabled="isTimeDisabled(msg)" :items="minutes" label="分" dense outlined hide-details/>
            </div>
            <v-select v-if="msg.operation !== 'resend'" v-model="msg.status" :items="statusOptionsFor(msg)" :disabled="saving"
              label="發送進度" dense outlined hide-details class="care-edit-status"/>
            <span v-else class="text-caption">新增待發送排程</span>
          </div>
          <div v-else class="d-flex flex-wrap align-center mt-1" style="gap: 8px;">
            <span class="font-weight-bold">{{ msg.operation === 'resend' ? msg.originalTime.replace('T', ' ') : `${msg.date} ${msg.hour}:${msg.minute}` }}</span>
            <v-chip small dark :color="statusColor(msg.status)">{{ formatStatus(msg.status) }}</v-chip>
          </div>
          <p v-if="msg.resendResult" class="text-caption success--text mt-2 mb-0" role="status">已重新安排，原紀錄保留。此列不會再次提交。</p>
          <p v-else-if="msg.batchSubmitted" class="text-caption mt-2 mb-0" role="status">已提交更新，請關閉視窗後查看最新結果。</p>
          <p v-if="msg.saveError" class="text-caption error--text mt-2 mb-0" role="alert">{{ msg.saveError }}</p>
          <p v-if="!msg.canResend && msg.resendBlockReason" class="text-caption mt-2 mb-0">{{ msg.resendBlockReason }}</p>
        </div>
      </v-card-text>
      <v-card-actions class="care-edit-actions">
        <v-btn color="tiffany-9" :loading="saving" :disabled="saving" block dark elevation="0" @click="$emit('save')">儲存編輯</v-btn>
        <v-btn :disabled="saving" block outlined color="grey-5" @click="$emit('close')">關閉</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import { careStatusColor, careStatusLabel, careEditStatusOptions, careEditTimeDisabled } from '@/utils/careScheduleStatus';
export default {
  name: 'CareScheduleEditDialog',
  props: {
    value: Boolean, saving: Boolean,
    currentItem: { type: Object, default: null },
    editableMessages: { type: Array, default: () => [] },
    minDate: { type: String, default: '' },
  },
  data() {
    return {
      datePickerMenus: [],
      hours: Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0')),
      minutes: Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, '0')),
      operationOptions: [{ text: '重新安排發送', value: 'resend' }, { text: '取消原排程', value: 'edit' }],
    };
  },
  watch: { value() { this.datePickerMenus = this.editableMessages.map(() => false); } },
  methods: {
    statusColor: careStatusColor,
    formatStatus: careStatusLabel,
    statusOptionsFor: careEditStatusOptions,
    isTimeDisabled(msg) { return careEditTimeDisabled(msg, this.saving); },
    changeOperation(msg) { msg.status = msg.operation === 'edit' ? '3' : msg.originalStatus; },
  },
};
</script>
<style scoped>
.care-edit-card { max-height: min(700px, 90vh); }
.care-edit-title { min-width: 0; overflow-wrap: anywhere; }
.care-edit-row + .care-edit-row { border-top: 1px solid #eee; padding-top: 12px; }
.care-edit-fields { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.care-edit-date { flex: 1 1 150px; min-width: 0; }
.care-edit-time { display: flex; align-items: center; gap: 8px; flex: 1 1 190px; min-width: 0; }
.care-edit-time > .v-input { width: 80px; min-width: 0; }
.care-edit-status, .care-edit-operation { flex: 1 1 150px; min-width: 0; }
.care-edit-actions { flex-direction: column; gap: 10px; width: 100%; max-width: 332px; align-self: center; padding: 12px 16px; }
.care-edit-actions > .v-btn { margin-left: 0 !important; }
@media (max-width: 600px) {
  .care-edit-date, .care-edit-time, .care-edit-status, .care-edit-operation { flex-basis: 100%; width: 100%; }
  .care-edit-actions { max-width: none; }
}
</style>
